import { LoadingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Spin,
  TreeSelect,
  Switch,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetActiveScalesApi } from "../../../api/GetActiveScalesApi";
import { GetAllPlanGroupsApi } from "../../../api/GetAllPlanGroups";
import GetScaleUnitsByScaleApi from "../../../api/GetScaleUnitsByScaleApi";
import { GetTreeSelectUnitsApi } from "../../../api/GetTreeSelectUnitsApi";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import moment from "moment";
import EditTopPlanApi from "../../../api/EditTopPlanApi";
import { GetPerfCycleTopLevelApi } from "../../../api/perf-cycles/GetPerfCycleTopPlanApi";
import GetPlansDetailsApi from "../../../api/plans/GetPlanDetailsApi";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import PageLoader from "../../sub/PageLoader";
import { GetApproversApi } from "../../../api/approvals/GetApproversApi";
import { GetEvaluationsApi } from "../../../api/ehrm/GetEvaluationsApi";
import { GetEmployeeEvaluationsApi } from "../../../api/ehrm/GetEmployeeEvaluationsApi";
import { GetGoalGroupsApi } from "../../../api/ehrm/GetGoalGroupsApi";
import PhrmService from "../../../services/PhrmService";

const { Option } = Select;

const { TextArea } = Input;

function EditTopPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [unitsTree, setUnitsTree] = useState([]);
  const [topPlan, setTopPlan] = useState();
  const [planGroups, setPlanGroups] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [unitPositions, setUnitPositions] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const [isPercentageUpdate, setIsPercentageUpdate] = useState(false);
  const [isNegativeScale, setIsNegativeScale] = useState(false);
  const [supportsNegativeScale, setSupportsNegativeScale] = useState(true); // Track if current scale supports negative scale
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const [form] = Form.useForm();
  const [valuesChanged, setValuesChanged] = useState(false);
  const positionState = useSelector((state) => state.positionState.value);
  const [approvers, setApprovers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [goalGroups, setGoalGroups] = useState([]);
  const [selectedEvalId, setSelectedEvalId] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [isPhrmEnabled, setIsPhrmEnabled] = useState(false);
  const [fieldLabels, setFieldLabels] = useState({});
  const [hasCascadeChildren, setHasCascadeChildren] = useState(false);
  const [freshPlanData, setFreshPlanData] = useState(null);

  const handleCascadeFromSplitPlansChange = (checked) => {
    if (checked && hasCascadeChildren) {
      Modal.warning({
        title: 'Existing Cascade Plans',
        content: 'This plan has existing cascade plans. Enabling this feature will mark them as inactive and their progress will no longer contribute to this plan.',
        onOk: () => {
          form.setFieldsValue({ cascadeFromSplitPlans: checked });
          setValuesChanged(true);
        },
        onCancel: () => {
          form.setFieldsValue({ cascadeFromSplitPlans: false });
        }
      });
    } else {
      setValuesChanged(true);
    }
  };

  function handleCancel() {
    setSubmit(false);
    setUnitsTree([]);
    setPlanGroups([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    setIsPercentageUpdate(false);
    setIsNegativeScale(false);
    setSupportsNegativeScale(true);
    setTopPlan();
    form.resetFields();
    props.handleCancel();
    setValuesChanged(false);
  }

  useEffect(() => {
    if (props.visible) {
      getTopPlanDetails();
      getUnitsTree();
      getPlanGroups();
      getActiveScales();
      getUnitPositions();
      getApprovers();
      checkPhrmPermissions();
      loadExistingEhrmData();
      fetchFullPlanDetails();
    }
  }, [props.visible]);

  // Separate useEffect to handle form field setting after data is loaded
  useEffect(() => {
    if (props.visible && (props.editPlan || freshPlanData)) {
      // Use freshPlanData if available, otherwise use props.editPlan
      const planData = freshPlanData || props.editPlan;
      
      // Robust boolean conversion for isPercentageUpdate
      const isPercentageUpdateValue = Boolean(
        planData?.isPercentageUpdate === true || 
        planData?.isPercentageUpdate === 1 || 
        planData?.isPercentageUpdate === "1" ||
        planData?.isPercentageUpdate === "true"
      );
      
      // Robust boolean conversion for isNegativeScale
      const isNegativeScaleValue = Boolean(
        planData?.isNegativeScale === true || 
        planData?.isNegativeScale === 1 || 
        planData?.isNegativeScale === "1" ||
        planData?.isNegativeScale === "true"
      );
      
      // Set the state immediately for UI updates
      setIsPercentageUpdate(isPercentageUpdateValue);
      setIsNegativeScale(isNegativeScaleValue);
      
      // Use setTimeout to ensure form is ready
      setTimeout(() => {
        form.setFields([
          {
            name: "title",
            value: planData?.title,
          },
          {
            name: "description",
            value: planData?.description,
          },
          {
            name: "target",
            value: planData?.target,
          },
          {
            name: "isPercentageUpdate",
            value: isPercentageUpdateValue,
          },
          {
            name: "isNegativeScale",
            value: isNegativeScaleValue,
          },
          {
            name: "unitScale",
            value: planData?.scale?.scaleUnitId,
          },
          {
            name: "targetDate",
            value: planData?.targetDate
              ? moment(planData?.targetDate)
              : "",
          },
          {
            name: "finalized",
            value: planData?.finalized === 1 || planData?.finalized === "1" || planData?.finalized === true,
          },
          {
            name: "isActive",
            value: planData?.isActive === 1 || planData?.isActive === "1" || planData?.isActive === true || planData?.isActive === undefined,
          },
          {
            name: "ehrmEvalId",
            value: planData?.ehrmEvalId ? parseInt(planData.ehrmEvalId) : undefined,
          },
          {
            name: "ehrmGoalTypeId", 
            value: planData?.ehrmGoalTypeId ? parseInt(planData.ehrmGoalTypeId) : undefined,
          },
          {
            name: "ehrmGoalWeightage",
            value: planData?.ehrmGoalWeightage || undefined,
          },
          {
            name: "cascadeFromSplitPlans",
            value: planData?.cascadeFromSplitPlans === true || 
                   planData?.cascadeFromSplitPlans === 1 || 
                   planData?.cascadeFromSplitPlans === "1",
          },
        ]);
        
        // Force form to re-render by triggering a validation
        form.validateFields(['isNegativeScale']).catch(() => {});
      }, 100);
    }
  }, [props.visible, props.editPlan, freshPlanData]);

  async function getTopPlanDetails() {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      setTopPlan(apiResponse.data);
    }
  }

  async function getUnitsTree() {
    const apiResponse = await GetTreeSelectUnitsApi();
    if (apiResponse.message === "success") {
      setUnitsTree(apiResponse.units);

      form.setFields([
        {
          name: "unitId",
          value: props.editPlan?.unit?.unitId,
        },
      ]);
    }
  }

  async function getPlanGroups() {
    const apiResponse = await GetAllPlanGroupsApi();
    if (apiResponse.message === "success") {
      setPlanGroups(apiResponse.planGroups);
      form.setFields([
        {
          name: "planGroupId",
          value: props.editPlan?.planGroupId,
        },
      ]);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
      selectScale(props.editPlan?.scale?.scaleId);
      form.setFields([
        {
          name: "targetScale",
          value: props.editPlan?.scale?.scaleId,
        },
      ]);
    }
  }

  async function getUnitPositions() {
    const apiResponse = await GetUnitPositionsApi2(
      props.editPlan?.unit?.unitId
    );
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: props.editPlan?.assignedTo?.positionId,
        },
        {
          name: "empReference",
          value: props.editPlan?.assignedTo?.user?.empReferrenceId || '',
        },
      ]);
    }
  }

  async function getApprovers() {
    const apiResponse = await GetApproversApi(positionState?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      setApprovers(apiResponse?.data);
      form.setFields([
        {
          name: "approver",
          value: apiResponse?.data[0]?.id || "",
        },
      ]);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit approver. Try again later."
      );
    }
  }

  async function checkPhrmPermissions() {
    const phrmEnabled = await PhrmService.isPhrmEnabled();
    setIsPhrmEnabled(phrmEnabled);
    setFieldLabels(PhrmService.getFieldLabels(phrmEnabled));
  }

  async function selectAssignTo(positionId) {
    const position = unitPositions.find(p => p.id === positionId);
    
    if (position && position.user) {
      setAssignedUser(position.user);
      
      form.setFieldsValue({
        empReference: position.user.empReferrenceId || '',
      });
      
      if (position.user.empReferrenceId) {
        const evalResponse = await GetEmployeeEvaluationsApi(position.user.empReferrenceId);
        if (evalResponse.ok && evalResponse.data) {
          setEvaluations(evalResponse.data);
        } else {
          setEvaluations([]);
        }
      } else {
        setEvaluations([]);
      }
    } else {
      setAssignedUser(null);
      setEvaluations([]);
      form.setFieldsValue({
        empReference: '',
      });
    }
    
    form.setFieldsValue({
      ehrmEvalId: undefined,
      ehrmGoalTypeId: undefined,
      ehrmGoalWeightage: undefined,
    });
    setSelectedEvalId(null);
    setGoalGroups([]);
  }

  async function selectEvaluation(evalId) {
    setSelectedEvalId(evalId);
    
    const goalGroupResponse = await GetGoalGroupsApi(evalId);
    if (goalGroupResponse.ok && goalGroupResponse.data) {
      setGoalGroups(goalGroupResponse.data);
    } else {
      setGoalGroups([]);
    }
    
    form.setFieldsValue({
      ehrmGoalTypeId: undefined,
    });
  }

  async function fetchFullPlanDetails() {
    if (props.editPlan?.planId) {
      try {
        const apiResponse = await GetPlansDetailsApi(props.editPlan.planId, positionState?.id);
        if (apiResponse.message === "success") {
          const fullPlanData = apiResponse.plan;
          setFreshPlanData(fullPlanData); // Store fresh data for form field setting
          
          // If plan has EHRM data, fetch the evaluations and goal groups using the IDs
          if (fullPlanData.ehrmEvalId && fullPlanData.assignedTo?.user?.empReferrenceId) {
            // Fetch evaluations for the assigned user
            const evalResponse = await GetEmployeeEvaluationsApi(fullPlanData.assignedTo.user.empReferrenceId);
            if (evalResponse.ok && evalResponse.data) {
              setEvaluations(evalResponse.data);
            }

            // Fetch goal groups for the evaluation
            const goalGroupResponse = await GetGoalGroupsApi(fullPlanData.ehrmEvalId);
            if (goalGroupResponse.ok && goalGroupResponse.data) {
              setGoalGroups(goalGroupResponse.data);
            }

            // Set the selected evaluation ID to trigger goal group loading
            setSelectedEvalId(fullPlanData.ehrmEvalId);
          }
          
          // Update form with EHRM fields from full plan data
          form.setFieldsValue({
            ehrmEvalId: fullPlanData.ehrmEvalId ? parseInt(fullPlanData.ehrmEvalId) : undefined,
            ehrmGoalTypeId: fullPlanData.ehrmGoalTypeId ? parseInt(fullPlanData.ehrmGoalTypeId) : undefined,
            ehrmGoalWeightage: fullPlanData.ehrmGoalWeightage || undefined,
            empReference: fullPlanData.assignedTo?.user?.empReferrenceId || '',
          });
        }
      } catch (error) {
        // Error fetching full plan details
      }
    }
  }

  async function loadExistingEhrmData() {
    // If the plan has existing EHRM data, load the evaluations and goal groups
    if (props.editPlan?.assignedTo?.user?.empReferrenceId && props.editPlan?.ehrmEvalId) {
      try {
        // Load evaluations for the assigned user
        const evalResponse = await GetEmployeeEvaluationsApi(props.editPlan.assignedTo.user.empReferrenceId);
        if (evalResponse.ok && evalResponse.data) {
          setEvaluations(evalResponse.data);
        }

        // Load goal groups for the evaluation
        if (props.editPlan.ehrmEvalId) {
          const goalGroupResponse = await GetGoalGroupsApi(props.editPlan.ehrmEvalId);
          if (goalGroupResponse.ok && goalGroupResponse.data) {
            setGoalGroups(goalGroupResponse.data);
          }
        }
      } catch (error) {
        // Error loading existing EHRM data
      }
    }
  }

  function selectPlanGroup(value) {
    planGroups.forEach((group) => {
      if (value === group.id) {
        form.setFields([
          {
            name: "targetScale",
            value: group.scale?.id,
          },
        ]);
        selectScale(group.scale?.id);
      }
    });
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
          setSupportsNegativeScale(true); // Number supports negative scale
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
          setSupportsNegativeScale(true); // Percentage supports negative scale
        } else if (scale.name === "Currency") {
          setAddScaleUnit(true);
          setPercentage(false);
          setSupportsNegativeScale(true); // Currency supports negative scale
          addUnit = "add";
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          setSupportsNegativeScale(false); // Other scales don't support negative scale
          // If current scale doesn't support negative scale, disable it
          if (isNegativeScale) {
            setIsNegativeScale(false);
            form.setFieldsValue({ isNegativeScale: false });
          }
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];

    topPlan?.planLevelAttributes?.forEach((attribute) => {
      let attributeValue = values[attribute.id];
      
      // Handle dropdown values - skip empty arrays
      if (attribute.valueType === "dropdown") {
        if (Array.isArray(attributeValue) && attributeValue.length === 0) {
          // Skip empty dropdown selections
          return;
        }
      }
      
      // Skip undefined or null values for optional attributes
      if (attributeValue === undefined || attributeValue === null || attributeValue === "") {
        return;
      }
      
      attributesValues.push({
        attributeId: attribute.id,
        value: attributeValue,
      });
    });

    // Send EHRM fields if PHRM is enabled AND (new fields are filled OR plan already has EHRM data)
    const hasNewEhrmData = values.ehrmEvalId && values.ehrmGoalTypeId && values.ehrmGoalWeightage;
    const hasExistingEhrmData = props.editPlan?.ehrmEvalId || props.editPlan?.ehrmGoalId;
    const shouldSendToEhrm = isPhrmEnabled && (hasNewEhrmData || hasExistingEhrmData);

    const apiResponse = await EditTopPlanApi(
      props.editPlan?.planId,
      values.title,
      values.description || "",
      values.target,
      values.targetDate || "",
      values.assignTo,
      positionState?.id,
      values.approver || null,
      attributesValues,
      null, // strategyId - not used for top plans
      Boolean(values.finalized),
      Boolean(values.isActive),
      shouldSendToEhrm ? values.ehrmEvalId : undefined,
      shouldSendToEhrm ? values.ehrmGoalTypeId : undefined,
      shouldSendToEhrm ? values.ehrmGoalWeightage : undefined,
      values.cascadeFromSplitPlans,
      isPercentageUpdate,
      isNegativeScale
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "Plan updated successfully") {
      openNotificationSuccess("bottomRight", `${topPlanState} edited`);
      props.handleOk(apiResponse.data);
    } else if (apiResponse.message === "Plan update submitted for approval") {
      openNotificationSuccess(
        "bottomRight",
        `${topPlanState} update submitted for approval`
      );
      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit plan. Try again later."
      );
    }
    handleCancel();
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }} disabled>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    const dropdownAndBooleanAttributes = topPlan?.planLevelAttributes?.filter((attribute) => {
      return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
    }) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
      const planAttribute = props.editPlan?.planAttributeValue?.find(
        (item) => item.planAttributeId === attribute.id
      );

      if (attribute.valueType === "dropdown") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              initialValue={[]}
            >
              <Select
                mode="multiple"
                showSearch
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%", maxWidth: "300px" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                optionLabelProp="children"
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        );
      } else if (attribute.valueType === "boolean") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    const otherAttributes = topPlan?.planLevelAttributes?.filter((attribute) => {
      return !(attribute.valueType === "dropdown" || attribute.valueType === "boolean");
    }) || [];

    otherAttributes.forEach((attribute) => {
      const planAttribute = props.editPlan?.planAttributeValue?.find(
        (item) => item.planAttributeId === attribute.id
      );

      if (attribute.valueType === "text" || attribute.valueType === "number" || attribute.valueType === "double" || attribute.valueType === "date") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              rules={[
                {
                  validator: (_, value) => {
                    if (attribute.valueType === "double" && value !== undefined && value !== null) {
                      if (attribute.minValue !== null && value < attribute.minValue) {
                        return Promise.reject(`Minimum value is ${attribute.minValue}`);
                      }
                      if (attribute.maxValue !== null && value > attribute.maxValue) {
                        return Promise.reject(`Maximum value is ${attribute.maxValue}`);
                      }
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              {attribute.valueType === "text" ? (
                <Input style={{ width: "100%" }} placeholder={attribute.name} />
              ) : attribute.valueType === "number" ? (
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                />
              ) : attribute.valueType === "double" ? (
                <InputNumber
                  min={attribute.minValue || 0}
                  max={attribute.maxValue || undefined}
                  step={1 / Math.pow(10, attribute.decimalPrecision || 2)}
                  precision={attribute.decimalPrecision || 2}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                  formatter={(value) => {
                    if (!value) return '';
                    const precision = attribute.decimalPrecision || 2;
                    return parseFloat(value).toFixed(precision);
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    return parseFloat(value.replace(/[^\d.-]/g, ''));
                  }}
                />
              ) : attribute.valueType === "date" ? (
                <DatePicker style={{ width: "100%" }} placeholder={attribute.name} />
              ) : null}
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }

  // Set form values for attributes after component loads
  useEffect(() => {
    if (freshPlanData?.planAttributeValue && topPlan?.planLevelAttributes) {

      
      // Wait for dropdown options to be fully loaded
      setTimeout(() => {
        const formFields = [];
        
        freshPlanData.planAttributeValue.forEach((attribute) => {
          let fieldValue = "";
          
          // Find the template attribute to get the valueType and dropdown options
          const templateAttr = topPlan.planLevelAttributes.find(
            (attr) => attr.id === attribute.planAttributeId
          );
          
          if (!templateAttr) {
            return; // Skip if template attribute not found
          }
          
          // Initialize dropdown fields with empty array by default
          if (templateAttr.valueType === "dropdown") {
            fieldValue = [];
          }
          
          if (attribute.value !== null && attribute.value !== undefined) {
            if (templateAttr.valueType === "text") {
              fieldValue = attribute.text || "";
            } else if (templateAttr.valueType === "number") {
              fieldValue = attribute.number;
            } else if (templateAttr.valueType === "double") {
              fieldValue = parseFloat(attribute.double);
            } else if (templateAttr.valueType === "date") {
              fieldValue = attribute.date ? moment(attribute.date) : null;
            } else if (templateAttr.valueType === "dropdown") {
              // Backend now always returns normalized JSON array string
              let rawDropdownValue = attribute.dropdown || attribute.value;
              

              
              try {
                if (rawDropdownValue && typeof rawDropdownValue === 'string') {
                  // Parse the JSON array string from backend
                  const parsed = JSON.parse(rawDropdownValue);

                  
                  if (Array.isArray(parsed)) {
                    fieldValue = parsed;
                  } else {
                    fieldValue = [];
                  }
                } else {
                  fieldValue = [];
                }
                
                // Validate that the values exist in the available options
                const availableOptionIds = templateAttr.dropdownOptions?.map(opt => opt.id) || [];
                const validValues = fieldValue.filter(val => availableOptionIds.includes(val));
                
                // Use valid values only
                fieldValue = validValues;
              } catch (e) {
                // Error parsing dropdown value
                fieldValue = [];
              }
            } else if (templateAttr.valueType === "boolean") {
              fieldValue = Boolean(attribute.value);
            }
          } else {
            // Handle null/undefined values for specific types
            if (templateAttr.valueType === "dropdown") {
              fieldValue = [];
            }
          }
          
          const fieldName = templateAttr.id;
          

          
          formFields.push({
            name: fieldName,
            value: fieldValue,
          });
        });
        
        // Set all form fields at once
        if (formFields.length > 0) {
          form.setFields(formFields);
        }
      }, 1000); // Increased timeout to ensure dropdown options are loaded
    }
  }, [freshPlanData?.planAttributeValue, topPlan?.planLevelAttributes])

  return (
    <>
      <Modal
        title={`Edit ${topPlanState}`}
        visible={props.visible}
        onOk={props.handleOk}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <Form
          name="editPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
          onValuesChange={() => {
            setValuesChanged(true);
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Plan group*"
                name="planGroupId"
                rules={[
                  {
                    required: true,
                    message: "Please select a plan group!",
                  },
                ]}
              >
                <Select
                  disabled
                  showSearch
                  placeholder="Select plan group"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onSelect={(value) => selectPlanGroup(value)}
                >
                  {planGroups.map((group) => {
                    return (
                      <Option key={group.id} value={group.id}>
                        {group.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={fieldLabels.title || "Title*"}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={`Title`} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unitId"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={unitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  disabled={true}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            

            <Col span={12}>
              <Form.Item
                label="Target scale*"
                name="targetScale"
                rules={[
                  {
                    required: true,
                    message: "Please select a target scale!",
                  },
                ]}
              >
                <Select placeholder="Select a scale" disabled={true}>
                  {scales.map((scale) => {
                    return (
                      <Option key={scale.id} value={scale.id}>
                        {scale.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={fieldLabels.target || "Target*"}
                name="target"
                rules={[
                  {
                    required: true,
                    message: "Please enter target!",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  addonAfter={addScaleUnit ? unitSelector : percentage && "%"}
                  style={{ width: "100%" }}
                  formatter={(value) => {
                    if (!value) return '';
                    // Format number with commas for better readability
                    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    // Remove commas when parsing
                    return value.replace(/,/g, '');
                  }}
                />
              </Form.Item>
            </Col>

            {topPlan?.target !== undefined && topPlan?.target !== null && (
              <Col span={6}>
                <Form.Item 
                  label=" " 
                  name="isPercentageUpdate" 
                  valuePropName="checked"
                >
                  <div className="flex items-center">
                    <Checkbox
                      checked={isPercentageUpdate}
                      disabled={isNegativeScale} // Disable when negative scale is enabled
                      onChange={(e) => {
                        setIsPercentageUpdate(e.target.checked);
                        form.setFieldsValue({ isPercentageUpdate: e.target.checked });
                        setValuesChanged(true);
                      }}
                    >
                      Update by %
                    </Checkbox>

                    <Popover
                      placement="topRight"
                      content={
                        <div className="text-xs">When enabled, progress updates will be entered as percentages (0-100%)</div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500 text-xs ml-1">
                        <InfoCircleOutlined />
                      </div>
                    </Popover>
                  </div>
                </Form.Item>
              </Col>
            )}

            {topPlan?.target !== undefined && topPlan?.target !== null && supportsNegativeScale && (
              <Col span={6}>
                <Form.Item 
                  label=" " 
                  name="isNegativeScale" 
                  valuePropName="checked"
                >
                  <div className="flex items-center">
                    <Checkbox
                      disabled={isPercentageUpdate} // Disable when percentage update is enabled
                      onChange={(e) => {
                        setIsNegativeScale(e.target.checked);
                        setValuesChanged(true);
                      }}
                    >
                      Negative Scale
                    </Checkbox>

                    <Popover
                      placement="topRight"
                      content={
                        <div className="text-xs">
                          Enables countdown logic for loss reduction, debt paydown, and cost cutting scenarios. 
                          Progress shows remaining amount instead of achieved amount.
                          <br /><br />
                          <strong>Available for:</strong> Number, Currency, and Percentage scales only.
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500 text-xs ml-1">
                        <InfoCircleOutlined />
                      </div>
                    </Popover>
                  </div>
                </Form.Item>
              </Col>
            )}


            <Col span={12}>
              <Form.Item 
                label="Finalized" 
                name="finalized"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Yes" 
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item 
                label="Active" 
                name="isActive"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Yes" 
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>

            {topPlan?.description && (
              <Col span={24}>
                <Form.Item label={fieldLabels.description || "Description"} name="description">
                  <TextArea
                    showCount
                    maxLength={100}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {topPlan?.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {isPhrmEnabled && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Employee Reference"
                    name="empReference"
                  >
                    <Input 
                      placeholder="Auto-filled from assigned user" 
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Evaluation"
                    name="ehrmEvalId"
                    rules={[{ required: false }]}
                  >
                    <Select
                      placeholder="Select evaluation"
                      onChange={(value) => selectEvaluation(value)}
                      allowClear
                      disabled={!assignedUser?.empReferrenceId}
                    >
                      {evaluations.map((evaluation) => (
                        <Option key={evaluation.EVAL_ID} value={evaluation.EVAL_ID}>
                          {evaluation.EVAL_NAME}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Goal Group"
                    name="ehrmGoalTypeId"
                    rules={[{ required: false }]}
                  >
                    <Select 
                      placeholder="Select goal group" 
                      allowClear
                      disabled={!selectedEvalId}
                    >
                      {goalGroups.map((group) => (
                        <Option key={group.GGRP_ID} value={group.GGRP_ID}>
                          {group.GGRP_NAME}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Goal Weight (%)"
                    name="ehrmGoalWeightage"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      precision={2}
                      placeholder="0.00"
                      style={{ width: '100%' }}
                      disabled={!assignedUser?.empReferrenceId}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {renderDropdownAndBooleanAttributes()}
            {renderOtherAttributes()}

            {!topPlan && <PageLoader />}

            {positionState?.userRole?.name === "Manager" &&
              !!approvers?.length && (
                <Col span={12}>
                  <Form.Item
                    label="Approver"
                    name="approver"
                    rules={[
                      {
                        required: true,
                        message: "Please select approver!",
                      },
                    ]}
                  >
                    <Select
                      allowClear
                      showSearch
                      placeholder="Select approver"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                    >
                      {approvers?.map((approver) => {
                        return (
                          <Option key={approver?.id} value={approver?.id}>
                            {(approver?.designations?.abbreviation ||
                              approver?.designations?.name) +
                              "," +
                              approver?.units?.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
              )}

            {topPlan?.cascadeFromSplitPlansEnabled && (
              <Col span={24}>
                <Form.Item
                  label="Cascade from Split Plans"
                  name="cascadeFromSplitPlans"
                  valuePropName="checked"
                  tooltip="When enabled, split plans will contribute to this plan's progress and cascade plans cannot be created below this plan"
                >
                  <Switch 
                    checkedChildren="Enabled" 
                    unCheckedChildren="Disabled"
                    onChange={handleCascadeFromSplitPlansChange}
                  />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit || !valuesChanged}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default EditTopPlanModal;
